      
import { GOOGLE_MAPS_API_KEY } from 'components/Map'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { useEffect, useRef } from 'react'
import { Menu, Search, X } from 'react-feather'
import { Skeleton } from 'components/Skeleton'
import { summarizeMembers } from './memberFilters'
import useBottomSheet from './useBottomSheet'
import './spatial.css'

const toLatLngStr = (c) => `${c[1].toFixed(6)},${c[0].toFixed(6)}`

export const staticMapUrl = (items, size) => {
  const params = items
    .slice(0, 5)
    .map((el) => {
      const pts = (el?.plottingPoints || [])
        .map((p) => p?.plottingPoint?.coordinates)
        .filter((c) => c?.length)
      const stride = Math.ceil(pts.length / 40) || 1
      const path = pts.filter((_, i) => i % stride === 0).map(toLatLngStr)
      if (path.length > 2) {
        return `path=color:0xD92D20ff|weight:3|fillcolor:0xF9706655|${path.join('|')}|${path[0]}`
      }
      const c = el?.locationPoint?.coordinates
      return c?.length ? `markers=color:red|${toLatLngStr(c)}` : null
    })
    .filter(Boolean)
  if (!params.length) return null
  const zoom = params.every((p) => p.startsWith('markers')) ? '&zoom=17' : ''
  return `https://maps.googleapis.com/maps/api/staticmap?size=${size}&scale=2&maptype=satellite${zoom}&${params.join('&')}&key=${GOOGLE_MAPS_API_KEY}`
}

export const formatHa = (m2) =>
  `${newFormatThousandSeparator((parseFloat(m2) || 0) / 10000)} ha`

export const totalLandSize = (plots) =>
  formatHa(
    (plots || []).reduce((sum, item) => sum + (parseFloat(item.size) || 0), 0),
  )

export const initials = (name) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

export default function Sidebar({
  farmerList,
  selectedUserId,
  onSelectFarmer,
  isSidebarVisible,
  onToggleSidebar,
  loading,
  search,
  onSearchChange,
  isMobile,
  detail,
  detailKey,
  isFiltered,
  sourcePicker,
}) {
  const { sheetStyle, grabProps, resetSheet, openHalf } = useBottomSheet(
    isMobile,
    0.62,
    true,
  )
  useEffect(() => {
    if (search) openHalf()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])
  useEffect(() => {
    if (!isSidebarVisible) resetSheet()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidebarVisible])

  const listRef = useRef(null)
  useEffect(() => {
    if (!selectedUserId || !isSidebarVisible) return
    listRef.current
      ?.querySelector(`[data-user-id="${CSS.escape(String(selectedUserId))}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [selectedUserId, isSidebarVisible])

  const term = search.trim().toLowerCase()
  const results = (
    term
      ? farmerList.filter(
          (el) =>
            el?.name?.toLowerCase().includes(term) ||
            String(el?.phoneNumber || '').includes(term),
        )
      : [...farmerList]
  ).sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '', 'id', { sensitivity: 'base' }),
  )
  const summary = summarizeMembers(results)

  return (
    <div
      className={`gm-panel ${isSidebarVisible ? '' : 'gm-panel--collapsed'}`}
    >
      <div className="gm-search">
        <button
          type="button"
          className="gm-icon-btn"
          onClick={onToggleSidebar}
          aria-label={isSidebarVisible ? 'Tutup daftar' : 'Buka daftar'}
        >
          <Menu size={20} />
        </button>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => !isSidebarVisible && onToggleSidebar()}
          placeholder="Cari anggota"
          aria-label="Cari anggota"
        />
        <Search size={20} className="gm-search-icon" />
        {search ? (
          <button
            type="button"
            className="gm-icon-btn"
            onClick={() => onSearchChange('')}
            aria-label="Hapus pencarian"
          >
            <X size={20} />
          </button>
        ) : null}
      </div>

      {isSidebarVisible || !isMobile ? (
        <div
          className="gm-results"
          data-sheet
          style={sheetStyle}
          aria-hidden={!isSidebarVisible}
          inert={isSidebarVisible ? undefined : ''}
        >
          {isMobile ? (
            <div className="gm-sheet-grab" {...grabProps}>
              <div className="gm-sheet-handle" />
            </div>
          ) : null}
          {detail ? (
            <div
              key={`detail-${detailKey}`}
              className="gm-sheet-detail gm-swap-in-right spatial-scrollbar"
            >
              {detail}
            </div>
          ) : null}
          <div
            className="gm-results-body gm-swap-in-left"
            hidden={Boolean(detail)}
          >
            <div className="gm-results-source" {...grabProps}>
              {sourcePicker}
            </div>
            {results.length ? (
              <div className="gm-summary" {...grabProps}>
                <div className="gm-summary-stats">
                  {[
                    ['Anggota', newFormatThousandSeparator(summary.members)],
                    ['Lahan', newFormatThousandSeparator(summary.plots)],
                    ['Perkiraan total luas', formatHa(summary.area)],
                  ].map(([label, value]) => (
                    <div key={label} className="gm-summary-stat">
                      <span className="gm-summary-value">{value}</span>
                      <span className="gm-summary-label">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="gm-results-list spatial-scrollbar" ref={listRef}>
              {loading && !farmerList?.length ? (
                <div role="status" aria-label="Memuat data anggota">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="gm-result gm-result--skeleton">
                      <div className="gm-result-body">
                        <Skeleton width="55%" height="18px" />
                        <Skeleton width="80%" height="14px" />
                        <Skeleton width="45%" height="14px" />
                      </div>
                      <Skeleton width="88px" height="88px" borderRadius="8px" />
                    </div>
                  ))}
                </div>
              ) : !results.length ? (
                <div className="px-4 py-4 gm-muted">
                  {term || isFiltered
                    ? 'Tidak ada anggota yang sesuai dengan pencarian atau filter.'
                    : 'Tidak ada data lahan/lokasi yang ditemukan pada sumber ini.'}
                </div>
              ) : (
                results.map((el) => {
                  const thumb = el?.profileImg || el?.profilePic
                  return (
                    <div
                      key={el?.userId}
                      data-user-id={el?.userId}
                      role="button"
                      tabIndex={0}
                      className={`gm-result ${
                        selectedUserId === el?.userId ? 'gm-result--active' : ''
                      }`}
                      onClick={() => onSelectFarmer(el)}
                      onKeyDown={(e) => e.key === 'Enter' && onSelectFarmer(el)}
                    >
                      <div className="gm-result-body">
                        <div className="gm-result-title">{el?.name}</div>
                        {el?.plots ? (
                          <div className="gm-result-line">
                            {totalLandSize(el.plots)} · {el.plots.length} lahan
                          </div>
                        ) : (
                          <Skeleton width="70%" height="14px" />
                        )}
                        {el?.plots && !el.plots.length ? (
                          <div className="gm-result-line gm-warn">
                            Lokasi tidak ditemukan
                          </div>
                        ) : null}
                      </div>
                      <div className="gm-thumb">
                        {initials(el?.name)}
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={el?.name || ''}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

    

    